
    import { Component, Mixins, Vue } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapState } from 'vuex';
    import moment from 'moment';
    import LogbuchEintragDetail from '@/components/Logbuch/LogbuchEintragDetail.vue';
    import LogbuchRow, {
        Eintragsart,
        LogbuchCreateMessage,
        LogbuchDeleteMessage,
        LogbuchEintrag,
        LogbuchUpdateMessage,
        Methods,
        Priority,
        } from '@/types/Logbuch';
    import { GridConfig, GridFilters } from '@/types';
    import GridResize from '@/mixins/GridResize';
    import { LogbuchFilter } from '@/store/types';
    import { VGrid } from '@/types/Components';
    import {LoadUsersPayload} from '@/store/users';

    @Component({
        computed: {
            ...mapState('logbuch', ['filter']),
            ...mapState('users', ['users']),
            ...mapGetters('authentication', ['isAllowed']),
        },
        methods: {
            ...mapActions('patient', ['loadPatient']),
            ...mapActions('users', ['loadUsers']),
        },
        components: {
            LogbuchEintragDetail,
        },
    })
    export default class Logbuch extends Mixins(Vue, GridResize) {
        public $refs!: {
            grid: VGrid<LogbuchRow>;
        };

        public filter!: LogbuchFilter;
        public loadPatient!: (payload: { patnr: number }) => Promise<void>;
        public loadUsers!: (payload?: LoadUsersPayload) => void;

        public zeitraumMenu = null;
        public eintragVisible = false;
        public selectedLogeintrag: LogbuchRow | null = null;
        public filterUsers = {
            deaktiviert: 'n'
        }

        public loglevel: { text: string; value: Priority }[] = [
            { text: 'Emergency', value: Priority.Emerg },
            { text: 'Alert', value: Priority.Alert },
            { text: 'Critical', value: Priority.Crit },
            { text: 'Error', value: Priority.Err },
            { text: 'Warning', value: Priority.Warn },
            { text: 'Notice', value: Priority.Notice },
            { text: 'Info', value: Priority.Info },
            { text: 'Debug', value: Priority.Debug },
        ];

        public itemAktionen: { text: string; value: Methods }[] = [
            { text: 'Abbruch', value: Methods.Cancel },
            { text: 'Änderung', value: Methods.Update },
            { text: 'Bericht', value: Methods.Druck },
            { text: 'Elga Eintrag', value: Methods.ElgaCreate },
            { text: 'Elga Storno', value: Methods.ElgaCancel },
            { text: 'Elga Übertragung', value: Methods.ElgaUebertragung },
            { text: 'Elga Zugriff', value: Methods.Elga },
            { text: 'Email', value: Methods.Email },
            { text: 'Export', value: Methods.Export },
            { text: 'Kassa', value: Methods.Kassa },
            { text: 'Lager', value: Methods.Lager },
            { text: 'Löschung', value: Methods.Delete },
            { text: 'Neuanlage', value: Methods.Create },
            { text: 'Rechnung', value: Methods.Rechnung },
            { text: 'Storno', value: Methods.Storno },
            { text: 'WAVM Export', value: Methods.Wavm },
            { text: 'Zugriff', value: Methods.Zugriff },
            { text: 'Übertragung', value: Methods.Transfer },
            { text: 'SMS', value: Methods.Sms },
        ];

        public mounted() {
            this.loadUsers({filter: this.filterUsers });
        }

        public itemFunctions: { text: string; value: string }[] = Object
            .keys(Eintragsart)
            .map((key: string) => ({ text: key, value: key }));

        public get gridConfig(): GridConfig<LogbuchRow> {
            return {
                id: 'logbuch',
                title: 'Logbuch',
                class: LogbuchRow,
                remote: '/verwaltung/logbuch',
                headers: [
                    {
                        value: 'timestamp',
                        text: 'Zeitstempel',
                        width: 175,
                    },
                    {
                        value: 'extra_user_id',
                        text: 'Benutzer',
                        width: 220,
                    },
                    {
                        value: 'priority',
                        text: 'Priorität',
                        width: 120,
                    },
                    {
                        value: 'extra_patnr',
                        text: 'Pat. Nr.',
                        width: 100,
                    },
                    {
                        value: 'extra_aktion',
                        text: 'Aktion',
                        width: 120,
                    },
                    {
                        value: 'extra_function',
                        text: 'Eintragsart',
                        width: 120,
                    },
                    {
                        value: 'actions',
                        text: '',
                        sortable: false,
                        width: 50,
                    },
                    {
                        value: 'message',
                        text: 'Nachricht',
                    },
                ],
            };
        }

        public get filters(): GridFilters<LogbuchRow> {
            const [minZeitraum, maxZeitraum] = [...this.filter.zeitraum].sort((a, b) => a.localeCompare(b));
            return [
                {
                    property: 'timestamp',
                    operator: 'between',
                    type: 'date',
                    value: {
                        min: minZeitraum,
                        max: maxZeitraum,
                    },
                },
                {
                    property: 'priority',
                    operator: 'in',
                    value: this.filter.loglevel,
                },
                {
                    property: 'extra_patnr',
                    operator: 'eq',
                    value: this.filter.patnr,
                },
                {
                    property: 'extra_aktion',
                    operator: 'eq',
                    value: this.filter.aktion,
                },
                {
                    property: 'extra_user_id',
                    operator: 'eq',
                    value: this.filter.user_id,
                },
                {
                    property: 'extra_function',
                    operator: 'eq',
                    value: this.filter.function,
                },
            ];
        }

        public getLoglevelName(item: LogbuchRow): string {
            return this.loglevel.find((loglevel) => loglevel.value === item.priority)?.text ?? '';
        }

        public getAktionname(id: number): string {
            return this.itemAktionen.find((item) => item.value === id)?.text ?? '';
        }

        public onClickMessageText(item: LogbuchRow): void {
            this.selectedLogeintrag = item;
            this.eintragVisible = true;
        }

        public showEintrag(item: LogbuchRow): boolean {
            if (item.isCreate) {
                const message = item.message as LogbuchCreateMessage;
                return (message.created?.length ?? 0) > 0;
            }

            if (item.isUpdate) {
                const message = item.message as LogbuchUpdateMessage;
                return (message.changes?.length ?? 0) > 0;
            }

            if (item.isDelete) {
                const message = item.message as LogbuchDeleteMessage;
                return (message.deleted?.length ?? 0) > 0;
            }

            return false;
        }

        public messageText(item: LogbuchRow): string {
            return (item.message as LogbuchEintrag).text ?? item.message as string;
        }

        public get maxZeitraum(): string {
            return moment().format('YYYY-MM-DD');
        }

        public get zeitraumLabel(): string {
            return this.filter.zeitraum.length
                ? [...this.filter.zeitraum].sort((a: string, b: string) => a.localeCompare(b)).map((date: string) => {
                    const [year, month, day] = date.split('-');
                    return `${day}.${month}.${year}`;
                }).join(' - ')
                : '';
        }

        public onClickPatient(item: LogbuchRow): void {
            this.$router.push('/');
            this.loadPatient({ patnr: item.extra_patnr as number });
        }

        public onClickFiltern(): void {
            this.$refs.grid.load(true);
        }

        public onLoadStorageFilters(filters?: GridFilters<LogbuchRow>): void {
            (filters ?? []).forEach((filter) => {
                switch (filter.property) {
                    case 'priority':
                        this.filter.loglevel = filter.value;
                        break;
                    case 'extra_patnr':
                        this.filter.patnr = filter.value;
                        break;
                    case 'extra_aktion':
                        this.filter.aktion = filter.value;
                        break;
                    case 'extra_function':
                        this.filter.function = filter.value;
                        break;
                    case 'extra_user_id':
                        this.filter.user_id = filter.value;
                        break;
                }
            });
        }
    }
